import { FormControl, FormControlProps, FormHelperText, useThemeProps } from '@mui/material';
import { DatePickerProps } from '@mui/x-date-pickers';
import { FORM_FIELD_THEME_NAME } from '@onevision-resources/design-theme';
import { Dayjs } from 'dayjs';
import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker } from 'src/components/DatePicker';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { useWatchConditions } from 'src/components/Forms/use-validations';

export type FormDatePickerProps = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'name' | 'type'
> & {
  name: string;
  label?: string;
  required?: boolean | string;
  datePickerProps?: Partial<DatePickerProps<Dayjs>>;
  value?: Dayjs | null;
  showErrorText?: boolean;
  rules?: Record<string, unknown>;
} & CommonFormFieldProps<'datepicker', Dayjs>;

export const FormDatePicker = (inProps: FormDatePickerProps) => {
  const props = useThemeProps({ props: inProps, name: FORM_FIELD_THEME_NAME });
  const {
    name,
    label,
    required,
    datePickerProps,
    value,
    fullWidth,
    showsIf = [],
    rules,
    showErrorText,
    disabled,
    labelAbove = false,
    size,
    'data-testid': testId,
  } = props;
  const generatedInputId = useId();
  const textFieldSlotProps = datePickerProps?.slotProps?.textField;
  const inputId =
    (typeof textFieldSlotProps === 'object' ? textFieldSlotProps.id : undefined) ??
    generatedInputId;

  const { control, formState, getFieldState } = useFormContext();
  const errorMsg = getFieldState(name, formState).error?.message;

  const shouldShow = useWatchConditions({
    control,
    showsIf,
  });

  if (!shouldShow) {
    return null;
  }

  return (
    <FormControl error={!!errorMsg} fullWidth={fullWidth} size={size} data-testid={testId}>
      {labelAbove && !!label ? (
        <FormFieldLabel htmlFor={inputId} disabled={disabled}>
          {label}
        </FormFieldLabel>
      ) : null}
      <Controller
        name={name}
        control={control}
        rules={{ ...rules, required }}
        render={({ field }) => (
          <DatePicker
            label={labelAbove ? undefined : label}
            errorMsg={errorMsg ? String(errorMsg) : undefined}
            size={size}
            datePickerProps={datePickerProps}
            slots={datePickerProps?.slots}
            {...field}
            value={(field.value as Dayjs) ?? value}
            disabled={disabled}
            slotProps={{
              ...datePickerProps?.slotProps,
              ...(labelAbove
                ? { textField: { ...datePickerProps?.slotProps?.textField, id: inputId } }
                : {}),
            }}
          />
        )}
      />
      {!!showErrorText && !!errorMsg && <FormHelperText>{String(errorMsg)}</FormHelperText>}
    </FormControl>
  );
};
